import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  History as HistoryIcon,
  Create as CreateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { Rule } from '../../types/rules';

interface RuleHistoryEntry {
  id: string;
  action: string;
  timestamp: string;
  userId: string;
  details: Record<string, unknown>;
}

interface RuleHistoryDialogProps {
  open: boolean;
  rule: Rule | null;
  onClose: () => void;
  onGetHistory: (ruleId: string) => Promise<{ ruleId: string; history: RuleHistoryEntry[] }>;
}

const RuleHistoryDialog: React.FC<RuleHistoryDialogProps> = ({
  open,
  rule,
  onClose,
  onGetHistory
}) => {
  const [history, setHistory] = useState<RuleHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && rule) {
      loadHistory();
    }
  }, [open, rule]);

  const loadHistory = async () => {
    if (!rule) return;

    setLoading(true);
    setError(null);

    try {
      const result = await onGetHistory(rule.id);
      setHistory(result.history);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rule history');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'created':
        return <CreateIcon />;
      case 'update':
      case 'updated':
        return <EditIcon />;
      case 'delete':
      case 'deleted':
        return <DeleteIcon />;
      case 'test':
      case 'tested':
        return <PlayIcon />;
      case 'execute':
      case 'executed':
        return <CheckCircleIcon />;
      case 'error':
      case 'failed':
        return <ErrorIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  const getActionColor = (action: string): 'inherit' | 'grey' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'created':
        return 'success';
      case 'update':
      case 'updated':
        return 'primary';
      case 'delete':
      case 'deleted':
        return 'error';
      case 'test':
      case 'tested':
        return 'info';
      case 'execute':
      case 'executed':
        return 'success';
      case 'error':
      case 'failed':
        return 'error';
      default:
        return 'grey';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getUserInitials = (userId: string) => {
    // Extract initials from user ID or name
    if (userId.includes('@')) {
      return userId.split('@')[0].substring(0, 2).toUpperCase();
    }
    return userId.substring(0, 2).toUpperCase();
  };

  const renderHistoryDetails = (entry: RuleHistoryEntry) => {
    const details = entry.details;
    
    if (!details || Object.keys(details).length === 0) {
      return null;
    }

    return (
      <Card variant="outlined" sx={{ mt: 1, bgcolor: 'grey.50' }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Details:
          </Typography>
          
          {typeof details.ruleName === 'string' && (
            <Typography variant="body2">
              <strong>Rule:</strong> {details.ruleName}
            </Typography>
          )}
          
          {typeof details.category === 'string' && (
            <Box sx={{ mt: 0.5 }}>
              <Chip label={details.category} size="small" variant="outlined" />
            </Box>
          )}
          
          {typeof details.oldVersion === 'string' && typeof details.newVersion === 'string' && (
            <Typography variant="body2">
              <strong>Version:</strong> {details.oldVersion} → {details.newVersion}
            </Typography>
          )}
          
          {typeof details.testPassed === 'boolean' && (
            <Box sx={{ mt: 0.5 }}>
              <Chip 
                label={details.testPassed ? 'Test Passed' : 'Test Failed'} 
                size="small" 
                color={details.testPassed ? 'success' : 'error'}
              />
            </Box>
          )}
          
          {typeof details.executionTime === 'number' && (
            <Typography variant="body2">
              <strong>Execution Time:</strong> {details.executionTime}ms
            </Typography>
          )}
          
          {typeof details.error === 'string' && (
            <Alert severity="error" sx={{ mt: 1 }}>
              <Typography variant="body2">{details.error}</Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Rule History: {rule?.name}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && history.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No history entries found for this rule.
            </Typography>
          </Box>
        )}

        {!loading && !error && history.length > 0 && (
          <Timeline>
            {history.map((entry, index) => {
              const { date, time } = formatTimestamp(entry.timestamp);
              const isLast = index === history.length - 1;
              
              return (
                <TimelineItem key={entry.id}>
                  <TimelineSeparator>
                    <TimelineDot color={getActionColor(entry.action)}>
                      {getActionIcon(entry.action)}
                    </TimelineDot>
                    {!isLast && <TimelineConnector />}
                  </TimelineSeparator>
                  
                  <TimelineContent>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                              {entry.action.charAt(0).toUpperCase() + entry.action.slice(1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {date} at {time}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                              {getUserInitials(entry.userId)}
                            </Avatar>
                            <Typography variant="body2" color="text.secondary">
                              {entry.userId}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {renderHistoryDetails(entry)}
                      </CardContent>
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleHistoryDialog;